import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableWithoutFeedback, View } from 'react-native';

import { getActorDisplayName } from '@components/ActivityList/activityHelpers';
import Avatar from '@components/Avatar/Avatar';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import Tag from '@components/shared/Tags/Tag.web';
import TaskCard from '@components/Tasks/TaskCard.web';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { TooltipOnboardingWeb } from '@components/Web/TooltipOnboarding.web';
import { User } from '@graphql/generated';
import useSearch from '@hooks/useSearch';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { ProjectTagType } from '@src/types/project';
import { TaskTagType } from '@src/types/task';
import { truncateForActivityFeed } from '@utils/ellipsizeByCharCount';

export type TagsCollectionType = {
  tasks: TaskTagType[];
  project: ProjectTagType;
  author: User;
};

export interface TagsCollectionProps {
  filterVal?: string;
  tagsCollection: TagsCollectionType;
  showAuthor: boolean;
  onDelete?: (index: number) => void;
  onDeleteProject?: () => void;
  onLongPress?: () => void;
  borderless?: boolean;
  isRow?: boolean;
  fromImagesPreview?: boolean;
  event?: boolean;
  externalNavigate?: any;
  doNotUseGlobalSearch?: boolean;
  isImagesPreview?: boolean;
  showProject?: boolean;
  type?: string;
  isJoinRequestPending?: boolean;
  /** When true, renders tags in a horizontal row, truncates names to 20 chars
   *  (first 19 + '...'), and removes paddingHorizontal (unless this is a
   *  join-request card). */
  isActivityItem?: boolean;
  hasPaddingLeft?: boolean;
  /** When true, drops the outline and inner padding so the tags sit directly
   *  in the surrounding chat bubble. */
  flush?: boolean;
  /** When true, drops the bottom margin of the last chip so the parent bubble
   *  padding alone controls the gap to the bubble edge. */
  trimBottomGap?: boolean;
  /** When true and the coachmark has not been seen, tapping a chip shows the
   *  tag-pill coachmark tooltip instead of navigating immediately. */
  coachmarkEnabled?: boolean;
  /** Called once (immediately) when a chip is tapped and the coachmark is
   *  shown, so the parent can mark the coachmark as seen. */
  onCoachmarkSeen?: () => void;
}

const TagsCollection: React.FC<TagsCollectionProps> = ({
  filterVal = '',
  tagsCollection,
  showAuthor,
  onDelete,
  onDeleteProject,
  onLongPress,
  borderless,
  isRow,
  fromImagesPreview = false,
  doNotUseGlobalSearch = false,
  event = '',
  externalNavigate,
  isImagesPreview = false,
  showProject = true,
  type,
  isJoinRequestPending = false,
  isActivityItem = false,
  hasPaddingLeft,
  flush,
  trimBottomGap = false,
  coachmarkEnabled = false,
  onCoachmarkSeen,
}) => {
  const { tasks, project, author } = tagsCollection;

  const { navigateToTask, navigateToProject } = useAppNavigation();
  const { isTaskWebPanelOpen, setIsTaskWebPanelOpen } = useWebDrawer();
  const taskLength = tasks.filter(
    (t) => t.deletedAt && t.deletedAt !== null
  ).length;
  const { t } = useTranslation();
  // Same actor-naming rule as the activity feed this renders inside, so the
  // two cannot drift (TA-10531 review). Covers a null author and an author
  // whose name fields are all empty, which previously reached Avatar as
  // undefined.
  const name = getActorDisplayName(author, t('shared:unknownUser'));
  const { search } = useSearch();
  let xfilterVal = filterVal ?? search;
  if (doNotUseGlobalSearch) xfilterVal = filterVal ?? '';

  // Coachmark state — local to this message's tags collection
  const tagsBoxRef = useRef<View>(null);
  const [coachmarkOpen, setCoachmarkOpen] = useState(false);
  const [pendingNav, setPendingNav] = useState<
    | { type: 'project'; item: ProjectTagType }
    | { type: 'task'; item: TaskTagType }
    | null
  >(null);

  const handleChipPress = (
    chipType: 'project' | 'task',
    item: ProjectTagType | TaskTagType
  ) => {
    if (coachmarkEnabled) {
      setPendingNav(
        chipType === 'project'
          ? { type: 'project', item: item as ProjectTagType }
          : { type: 'task', item: item as TaskTagType }
      );
      setCoachmarkOpen(true);
      onCoachmarkSeen?.();
    } else if (chipType === 'project') {
      isTaskWebPanelOpen && setIsTaskWebPanelOpen(false);
      if (externalNavigate) externalNavigate.navigateToProject(item);
      else navigateToProject(item as ProjectTagType);
    } else {
      navigateToTask({
        ...item,
        project: project
          ? {
              uuid: project.id,
              name: project.name,
            }
          : (item as TaskTagType).project,
      } as TaskTagType);
    }
  };

  const handleCoachmarkGotIt = () => {
    setCoachmarkOpen(false);
    if (pendingNav) {
      if (pendingNav.type === 'project') {
        isTaskWebPanelOpen && setIsTaskWebPanelOpen(false);
        if (externalNavigate)
          externalNavigate.navigateToProject(pendingNav.item);
        else navigateToProject(pendingNav.item as ProjectTagType);
      } else {
        navigateToTask({
          ...pendingNav.item,
          project: project
            ? {
                uuid: project.id,
                name: project.name,
              }
            : pendingNav.item.project,
        } as TaskTagType);
      }
      setPendingNav(null);
    }
  };

  const marginLeft = isImagesPreview ? 'm' : 'xs';

  // When isActivityItem=true: first 19 chars + '...' via shared utility;
  // otherwise use the original 25-char regex for the non-activity layout.
  const projectLabel = isActivityItem
    ? truncateForActivityFeed(project?.name ?? '')
    : project?.name?.replace(new RegExp(`(.{${25 - 1}})..+`), '$1...');

  const taskLabel = (taskName: string) =>
    isActivityItem ? truncateForActivityFeed(taskName ?? '') : taskName;

  const labelVariant = isImagesPreview ? 'metadata' : 'labelSmall';
  const bodyPaddingTop = isActivityItem ? 'xxs' : 'xs';
  const flushPaddingTop = flush ? 'none' : bodyPaddingTop;
  const chipMarginBottom = isActivityItem ? 'xxs' : 'xs';
  const isProjectChipLast = tasks.length === 0;
  const projectChipMarginBottom =
    trimBottomGap && isProjectChipLast ? 'none' : chipMarginBottom;
  const getTaskChipMarginBottom = (index: number) =>
    trimBottomGap && index === tasks.length - 1 ? 'none' : chipMarginBottom;

  if (event === 'TaskCompleted') {
    return (
      <Box>
        <TaskCard title='Task Completed'>
          <>
            <TouchableWithoutFeedback onLongPress={onLongPress}>
              <Box flexDirection='row' alignItems='center'>
                {showAuthor && (
                  <Box marginRight='s'>
                    <TouchableWithoutFeedback onLongPress={onLongPress}>
                      {author ? (
                        <Avatar
                          avatar={author.avatar}
                          size='small'
                          label={name}
                        />
                      ) : (
                        <Avatar id='' avatar={null} label='I' size='xs' />
                      )}
                    </TouchableWithoutFeedback>
                  </Box>
                )}
                {project?.name && (
                  <Box
                    accessibilityLabel='Tagged item'
                    flexWrap='wrap'
                    flexDirection={
                      fromImagesPreview || isActivityItem ? 'row' : 'column'
                    }
                    borderWidth={0}
                    borderRadius='xs'
                    px={!isRow || hasPaddingLeft ? 'xs' : undefined}
                    paddingTop={isActivityItem ? 'xxs' : 'xs'}>
                    <Box maxWidth='100%' flexDirection='row'>
                      <Tag
                        filterVal={xfilterVal}
                        labelVariant={
                          isActivityItem ? 'metadataSecondaryNew' : labelVariant
                        }
                        variant='secondaryReversed'
                        prefixMarginRight={isActivityItem ? 'xxxs' : 'xxs'}
                        padding='xxs'
                        marginBottom={isActivityItem ? 'xxs' : 'xs'}
                        iconWidth={isActivityItem ? 12 : undefined}
                        iconHeight={isActivityItem ? 12 : undefined}
                        borderRadius='xxs'
                        label={projectLabel}
                        title={isActivityItem ? project?.name : undefined}
                        prefix={
                          <Icon
                            variant={isActivityItem ? 'xxs' : 'xs'}
                            name='Folder'
                            color='white'
                          />
                        }
                        onPress={() => {
                          isTaskWebPanelOpen && setIsTaskWebPanelOpen(false);
                          if (externalNavigate)
                            externalNavigate.navigateToProject(project);
                          else navigateToProject(project);
                        }}
                        onLongPress={onLongPress}
                        onDismiss={
                          tasks.length === 0 ? onDeleteProject : undefined
                        }
                      />
                      <Box flex={1}></Box>
                    </Box>
                    {tasks?.map((t, i) => (
                      <Box
                        key={t.id}
                        maxWidth='100%'
                        flexDirection='row'
                        ml={
                          fromImagesPreview || (isRow && showProject)
                            ? marginLeft
                            : 'none'
                        }
                        mr={isRow && !showProject ? marginLeft : 'none'}>
                        <Tag
                          isActivityItem={isActivityItem}
                          filterVal={xfilterVal}
                          labelVariant={
                            isActivityItem
                              ? 'metadataSecondaryNew'
                              : labelVariant
                          }
                          prefixMarginRight={isActivityItem ? 'xxxs' : 'xxs'}
                          variant={t.deletedAt ? 'folder' : 'reversed'}
                          padding='xxs'
                          borderRadius='xxs'
                          label={taskLabel(t?.name)}
                          title={isActivityItem ? t?.name : undefined}
                          marginBottom={isActivityItem ? 'xxs' : 'xs'}
                          iconWidth={isActivityItem ? 12 : undefined}
                          iconHeight={isActivityItem ? 12 : undefined}
                          prefix={
                            <Icon
                              variant={isActivityItem ? 'xxs' : 'xs'}
                              name='Hash'
                              color={t.deletedAt ? 'grey04' : 'white'}
                            />
                          }
                          onPress={() => {
                            !t.deletedAt &&
                              navigateToTask({
                                ...t,
                                project: project
                                  ? {
                                      uuid: project.id,
                                      name: project.name,
                                    }
                                  : t.project,
                              });
                          }}
                          onLongPress={() => !t.deletedAt && onLongPress}
                          onDismiss={onDelete ? () => onDelete(i) : undefined}
                          type='tasks'
                          deletedAt={t.deletedAt}
                        />
                        <Box flex={1}></Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </TouchableWithoutFeedback>

            {taskLength > 0 && !isActivityItem && (
              <Box
                flex={1}
                flexDirection='row'
                alignSelf='flex-end'
                marginBottom='none'
                style={{ cursor: 'default' }}
                marginVertical='s'>
                <Icon name='Trash2' color='grey03' width={18} height={18} />
                <Text variant='link' color='grey04' marginLeft='xxs'>
                  {taskLength == 1
                    ? t('shared:taskSingleDelete')
                    : t('shared:taskMultiDelete')}
                </Text>
                <Box flex={1}></Box>
              </Box>
            )}
            <Box
              flex={1}
              alignSelf='flex-end'
              marginHorizontal='s'
              marginVertical='xxs'>
              <Avatar
                id={tasks?.[0]?.completor?.id}
                avatar={tasks?.[0]?.completor?.avatar}
                label={tasks?.[0]?.completor?.name}
                size='xs'
              />
            </Box>
          </>
        </TaskCard>
      </Box>
    );
  }

  return (
    <>
      <TouchableWithoutFeedback onLongPress={onLongPress}>
        <Box flexDirection='row' alignItems='center'>
          {showAuthor && (
            <Box marginRight='s'>
              <TouchableWithoutFeedback onLongPress={onLongPress}>
                {author ? (
                  <Avatar avatar={author.avatar} size='small' label={name} />
                ) : (
                  <Avatar id='' avatar={null} label='I' size='xs' />
                )}
              </TouchableWithoutFeedback>
            </Box>
          )}
          {project?.name && (
            <Box
              ref={tagsBoxRef as any}
              accessibilityLabel='Tagged item'
              flexWrap='wrap'
              flexDirection={
                fromImagesPreview || isActivityItem ? 'row' : 'column'
              }
              borderColor='grey02'
              borderWidth={!borderless && !flush ? 1 : 0}
              borderRadius='xs'
              px={!flush && (!isRow || hasPaddingLeft) ? 'xs' : undefined}
              paddingTop={flushPaddingTop}>
              {showProject && (
                <Box maxWidth='100%' flexDirection='row'>
                  <Tag
                    filterVal={xfilterVal}
                    labelVariant={
                      isActivityItem ? 'metadataSecondaryNew' : labelVariant
                    }
                    variant='secondaryReversed'
                    prefixMarginRight={isActivityItem ? 'xxs' : 'xxs'}
                    padding='xxs'
                    marginBottom={projectChipMarginBottom}
                    iconWidth={isActivityItem ? 12 : undefined}
                    iconHeight={isActivityItem ? 12 : undefined}
                    borderRadius='xxs'
                    label={projectLabel}
                    title={isActivityItem ? project?.name : undefined}
                    prefix={
                      <Icon
                        variant={isActivityItem ? 'xxs' : 'xs'}
                        name='Folder'
                        color='white'
                      />
                    }
                    onPress={() => {
                      handleChipPress('project', project);
                    }}
                    onLongPress={onLongPress}
                    onDismiss={tasks.length === 0 ? onDeleteProject : undefined}
                  />
                  <Box flex={1}></Box>
                </Box>
              )}
              {tasks?.map((t, i) => (
                <Box
                  accessibilityLabel={`Tagged item | ${t?.name}`}
                  key={t.id}
                  maxWidth='100%'
                  flexDirection='row'
                  ml={
                    fromImagesPreview || (isRow && showProject)
                      ? marginLeft
                      : 'none'
                  }
                  mr={isRow && !showProject ? marginLeft : 'none'}>
                  <Tag
                    isActivityItem={isActivityItem}
                    isMemberActivity={type === 'MemberActivity'}
                    filterVal={xfilterVal}
                    labelVariant={
                      isActivityItem ? 'metadataSecondaryNew' : labelVariant
                    }
                    prefixMarginRight={isActivityItem ? 'xxxs' : 'xxs'}
                    variant={t.deletedAt ? 'folder' : 'reversed'}
                    padding='xxs'
                    borderRadius='xxs'
                    label={taskLabel(t?.name)}
                    title={isActivityItem ? t?.name : undefined}
                    marginBottom={getTaskChipMarginBottom(i)}
                    iconWidth={isActivityItem ? 12 : undefined}
                    iconHeight={isActivityItem ? 12 : undefined}
                    prefix={
                      <Icon
                        variant={isActivityItem ? 'xxs' : 'xs'}
                        name='Hash'
                        color={t.deletedAt ? 'grey04' : 'white'}
                      />
                    }
                    onPress={() => {
                      if (!t.deletedAt) {
                        handleChipPress('task', t);
                      }
                    }}
                    onLongPress={
                      isJoinRequestPending
                        ? undefined
                        : () => !t.deletedAt && onLongPress
                    }
                    onDismiss={onDelete ? () => onDelete(i) : undefined}
                    type='tasks'
                    deletedAt={t.deletedAt}
                  />
                  <Box flex={1}></Box>
                </Box>
              ))}
            </Box>
          )}
          {/* Coachmark tooltip rendered via portal, positioned to the left of
              the chips box. Only mounted when active to keep DOM clean. */}
          {(coachmarkEnabled || coachmarkOpen) && project?.name && (
            <TooltipOnboardingWeb
              externalTriggerRef={tagsBoxRef}
              variant='left-center'
              tooltipStyle='success'
              open={coachmarkOpen}
              onOpenChange={() => {}}
              trigger='hover'
              title={t('models:chat.onboarding.tagPillCoachmark.title')}
              description={t(
                'models:chat.onboarding.tagPillCoachmark.description'
              )}
              ctaText={t('models:chat.onboarding.tagPillCoachmark.gotIt')}
              onCtaPress={handleCoachmarkGotIt}
              offset={12}>
              {/* Dummy zero-size child — positioning uses externalTriggerRef */}
              <View style={{ width: 0, height: 0 }} />
            </TooltipOnboardingWeb>
          )}
        </Box>
      </TouchableWithoutFeedback>

      {taskLength > 0 && !isActivityItem && (
        <Box
          flex={1}
          flexDirection='row'
          alignSelf='flex-end'
          marginBottom='none'
          style={{ cursor: 'default' }}
          marginVertical='s'>
          <Icon name='Trash2' color='grey03' width={18} height={18} />
          <Text variant='link' color='grey04' marginLeft='xxs'>
            {taskLength == 1
              ? t('shared:taskSingleDelete')
              : t('shared:taskMultiDelete')}
          </Text>
          <Box flex={1}></Box>
        </Box>
      )}
    </>
  );
};

export default TagsCollection;
